import { useState } from 'react'
import { useQuery, useAction } from 'convex/react'
import { api } from '../../../../../convex/_generated/api'
import { Card } from '@/components/ui/card'
import { RefreshCw } from 'lucide-react'
import { cn } from '@/lib/utils'
import { AttributionChart } from '../AttributionChart'
import {
  PAID_CHANNELS,
  RANGES,
  formatCurrency,
  formatDate,
  formatNumber,
  formatPct,
} from '../_shared'

export default function PaidAttributionPage() {
  const [days, setDays] = useState(30)
  const [showAds, setShowAds] = useState(true)
  const [refreshing, setRefreshing] = useState(false)

  const data = useQuery(api.attribution.getAttributionDashboard, {
    days,
    channels: PAID_CHANNELS,
  })
  const refreshAds = useAction(api.googleAdsActions.refreshNow)

  const onRefreshAds = async () => {
    setRefreshing(true)
    try {
      await refreshAds({ days: Math.max(days, 30) })
    } catch (err) {
      console.error('[PaidAttribution] refresh failed', err)
    } finally {
      setRefreshing(false)
    }
  }

  const isLoading = data === undefined
  const ads = data?.adsTotals ?? { cost: 0, conversions: 0, clicks: 0, impressions: 0 }
  const paidSignups = data?.stageByChannel.signups.paid_search ?? 0
  const paidPro = data?.stageByChannel.proConverted.paid_search ?? 0
  const paidVisitors = data?.stageByChannel.visitors.paid_search ?? 0
  const ctr = ads.impressions > 0 ? (ads.clicks / ads.impressions) * 100 : 0
  const cpc = ads.clicks > 0 ? ads.cost / ads.clicks : 0
  const costPerSignup = paidSignups > 0 ? ads.cost / paidSignups : 0
  const costPerPro = paidPro > 0 ? ads.cost / paidPro : 0

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Paid Attribution</h1>
          <p className="text-sm text-neutral-500">
            Google search ads — funnel + spend ·{' '}
            {data ? `${formatDate(data.startDate)} – ${formatDate(data.endDate)}` : '—'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-md border border-neutral-200 overflow-hidden">
            {RANGES.map((r) => (
              <button
                key={r.days}
                onClick={() => setDays(r.days)}
                className={cn(
                  'px-3 py-1.5 text-xs font-medium transition-colors',
                  days === r.days
                    ? 'bg-neutral-900 text-white'
                    : 'bg-white text-neutral-700 hover:bg-neutral-50',
                )}
              >
                {r.label}
              </button>
            ))}
          </div>
          <button
            onClick={onRefreshAds}
            disabled={refreshing}
            className="inline-flex items-center gap-1.5 rounded-md border border-neutral-200 bg-white px-3 py-1.5 text-xs font-medium text-neutral-700 hover:bg-neutral-50 disabled:opacity-50"
          >
            <RefreshCw className={cn('h-3.5 w-3.5', refreshing && 'animate-spin')} />
            Refresh ads
          </button>
        </div>
      </div>

      {/* Headline ad spend KPIs */}
      <Card className="overflow-hidden p-0">
        <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-neutral-200">
          <div className="px-4 py-4 bg-[#1A73E8] text-white">
            <div className="text-[11px] font-medium mb-1 uppercase tracking-wider text-white/85">
              ▾ Ad spend
            </div>
            <div className="text-2xl font-medium tabular-nums">
              {formatCurrency(ads.cost, { compact: ads.cost >= 1000 })}
            </div>
          </div>
          <div className="px-4 py-4 bg-[#D93025] text-white">
            <div className="text-[11px] font-medium mb-1 uppercase tracking-wider text-white/85">
              ▾ Cost / signup
            </div>
            <div className="text-2xl font-medium tabular-nums">
              {paidSignups > 0 ? formatCurrency(costPerSignup) : '—'}
            </div>
          </div>
          <div className="px-4 py-4">
            <div className="text-[11px] font-medium mb-1 uppercase tracking-wider text-neutral-500">
              ▾ Cost / pro
            </div>
            <div className="text-2xl font-medium tabular-nums text-neutral-900">
              {paidPro > 0 ? formatCurrency(costPerPro) : '—'}
            </div>
          </div>
          <div className="px-4 py-4">
            <div className="text-[11px] font-medium mb-1 uppercase tracking-wider text-neutral-500">
              ▾ Avg. CPC
            </div>
            <div className="text-2xl font-medium tabular-nums text-neutral-900">
              {ads.clicks > 0 ? formatCurrency(cpc) : '—'}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-neutral-200 border-t border-neutral-200 bg-neutral-50/60">
          <div className="px-4 py-2.5">
            <div className="text-[11px] text-neutral-500">Impressions</div>
            <div className="text-sm font-medium text-neutral-900 tabular-nums">
              {formatNumber(ads.impressions)}
            </div>
          </div>
          <div className="px-4 py-2.5">
            <div className="text-[11px] text-neutral-500">Clicks</div>
            <div className="text-sm font-medium text-neutral-900 tabular-nums">
              {formatNumber(ads.clicks)}
            </div>
          </div>
          <div className="px-4 py-2.5">
            <div className="text-[11px] text-neutral-500">CTR</div>
            <div className="text-sm font-medium text-neutral-900 tabular-nums">
              {ads.impressions > 0 ? formatPct(ctr) : '—'}
            </div>
          </div>
          <div className="px-4 py-2.5">
            <div className="text-[11px] text-neutral-500">Ad conversions</div>
            <div className="text-sm font-medium text-neutral-900 tabular-nums">
              {formatNumber(ads.conversions)}
            </div>
          </div>
        </div>
      </Card>

      <AttributionChart
        data={data}
        isLoading={isLoading}
        showAds={showAds}
        showChannelFilter={false}
        onShowAdsChange={setShowAds}
        emptyMessage="No paid traffic in range. Verify Google Ads tracking is firing."
      />

      {/* Visitor → Pro conversion summary */}
      {!isLoading && paidVisitors > 0 && (
        <Card className="p-4">
          <div className="text-sm text-neutral-700">
            <span className="font-semibold">{formatNumber(paidVisitors)}</span> paid-search
            visitors converted into <span className="font-semibold">{formatNumber(paidPro)}</span>{' '}
            pro subscribers —{' '}
            <span className="font-semibold text-emerald-600">
              {formatPct((paidPro / paidVisitors) * 100)}
            </span>{' '}
            visitor→pro rate.
          </div>
        </Card>
      )}
    </div>
  )
}
